/**
 * Path-only help context. Never accept query strings, hashes, or free text —
 * those can carry identifiers. Chat ranking and suggestions use the pathname.
 */

const SAFE_PATH = /^\/[A-Za-z0-9/_-]*$/;
const OPAQUE_SEGMENT =
  /^(?:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|[0-9a-f]{16,}|[0-9]{6,})$/i;

export function normalizeHelpChatPath(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;

  let value = raw.trim();
  if (!value) return null;

  try {
    if (/^https?:\/\//i.test(value)) {
      value = new URL(value).pathname;
    }
  } catch {
    return null;
  }

  const pathOnly = value.split('?')[0]?.split('#')[0] ?? '';
  if (!SAFE_PATH.test(pathOnly) || pathOnly.length > 180) return null;

  const collapsed = pathOnly.replace(/\/{2,}/g, '/').replace(/\/+$/, '');
  const stripped = collapsed
    .split('/')
    .filter((segment) => segment.length > 0 && !OPAQUE_SEGMENT.test(segment));
  return stripped.length > 0 ? `/${stripped.join('/')}` : '/';
}

export function helpPathOverlapScore(
  articlePath: string | null | undefined,
  currentPath: string | null | undefined,
): number {
  const article = normalizeHelpChatPath(articlePath);
  const current = normalizeHelpChatPath(currentPath);
  if (!article || !current) return 0;
  // The active screen is the strongest user intent. Richer articles can have
  // many more keyword occurrences, but must not displace the guide for the
  // screen the provider is currently using.
  if (article === current) return 200;
  if (current.startsWith(`${article}/`) || article.startsWith(`${current}/`)) return 36;

  const articlePrefix = pathPrefix(article, 2);
  const currentPrefix = pathPrefix(current, 2);
  if (articlePrefix !== '/' && articlePrefix === currentPrefix) return 12;
  return 0;
}

function pathPrefix(path: string, segments: number): string {
  const parts = path.split('/').filter(Boolean).slice(0, segments);
  return parts.length > 0 ? `/${parts.join('/')}` : '/';
}
